// IsomorphicService per ADR 010 — pure, read-only, generic-on-shape.
// THE buyer's-premium cascade resolver (BL-983).
//
// Replaces three divergent resolution chains that each answered "which level's
// buyer's premium applies?" differently:
//   • `isUsableBp` + its hand-rolled 3-arm chain in `invoice-repository.ts`
//     (lot → auction → tenant)
//   • `resolveEffectiveBuyersPremium` on the bidder (lot → auction, auction rung
//     ungated)
//   • `resolveCreateAuctionBuyersPremium` at auction creation (request → tenant)
// That divergence is the BUG-506 / BUG-619 / BUG-558 family: the number a buyer
// was shown and the number they were billed came from different chains. One
// resolver makes them the same by construction.
//
// ## The lot rung is gone (BL-991)
//
// A `lot` rung lived here transitionally because `auction_lots.buyers_premium` held
// live values from BUG-944's lot overrides; BL-990's reset NULLed every one of them
// and BL-991 DROPPED the column, so the rung has no source of data and is removed.
//
// Removing it is not cosmetic. While the rung existed, a stale lot row could
// outrank its ring — the wrongness BL-988 had to fix on the bidder by omitting the
// rung there. With the column dropped and the rung gone, that outcome is
// structurally impossible rather than merely unlikely.
//
// ## The house rung is gone too (BL-1382)
//
// The cascade is now **ring → auction**, in the type as well as in fact.
//
// BL-1381 stamps every auction with its own buyer's premium at creation, so an
// auction that exists always answers for itself. That makes a NULL
// `auctions.buyers_premium` mean "this auction charges no buyer's premium" — NOT
// "ask the house". A house rung beneath the auction would give that NULL a second
// meaning and re-open the two-answers-to-one-question shape ADR 044 exists to
// close, so `BuyersPremiumRungs` no longer declares `tenant`.
//
// The house default has no resolution role at all any more. It is the value a NEW
// auction is stamped with (BL-1381, ADR 032 §2) — nothing else. An ADR 028
// **unassociated** lot (`auction_id IS NULL`) used to reach it through a second,
// house-only entry point on this module; BUG-1549 removed the only surface that
// resolved a premium for such a lot, and BL-1673 deleted the entry point with it.
// No lot surface resolves or displays a premium for a lot with no auction.

import type { PremiumSchedule } from './schedule';
import { isUsableBuyersPremium, type BuyersPremiumTier, type BuyersPremiumLike } from './types';
import { isPremiumSchedule } from './wire';

/**
 * The structural minimum a cascade rung must expose for {@link resolveBuyersPremium}
 * to judge it — NOT a description of what is on disk.
 *
 * ADR 010 §2 requires an IsomorphicService to be generic on the *shape it needs*,
 * not on a concrete row type, so that admin's `BuyersPremiumSettings` and bidder's
 * public projection both satisfy it without a cast. Both of those declare `type` as a
 * widened union and their variant fields as optional, so constraining to the strict
 * `BuyersPremium` union here would reject both callers.
 *
 * This replaced `LegacyBuyersPremium` when BL-990 deleted the read-seam bridge. The
 * two types were structurally similar but meant different things: that one described
 * drifted on-disk data (and carried the retired `flatCap`); this one describes the
 * minimum a caller must hand us.
 */
export interface BuyersPremiumRungShape {
  type?: string;
  flatRate?: number | null;
  tiers?: BuyersPremiumTier[] | null;
}

/**
 * Which level supplied the resolved buyer's premium.
 *
 * Exactly the two rungs the cascade walks. There is no `'house'` member: BL-1382
 * removed the house rung from the walk, and BL-1673 deleted the last entry point
 * that could still report it, so a consumer switching on this union never has to
 * reason about a level nothing produces.
 */
export type BuyersPremiumSource = 'ring' | 'auction';

/** The resolved buyer's premium plus the level it came from. */
export interface ResolvedBuyersPremium<T> {
  /** The winning level's config, or `null` when no level carries one. */
  bp: T | null;
  /** Which level won, or `null` when none did. */
  source: BuyersPremiumSource | null;
}

/**
 * The two cascade levels, highest precedence first.
 *
 * There is deliberately no `tenant` key — BL-1382 removed the house rung (see the
 * module header).
 */
export interface BuyersPremiumRungs<T> {
  /**
   * Per-ring override (`auction_rings.buyers_premium`).
   *
   * CONTRACT: callers MUST pass `null` for a soft-deleted ring. This function is
   * pure and cannot read `deleted_at`, so a deleted ring is filtered at the read
   * — invoice resolution does it with `leftJoin(... AND deleted_at IS NULL)`
   * (BL-985). A deleted ring's override must never apply to its lots.
   */
  ring?: T | null;
  /**
   * The auction's own premium (`auctions.buyers_premium`), stamped at creation by
   * BL-1381. No longer an "override" — with no rung beneath it, it is simply what
   * this auction charges, and its absence means it charges nothing.
   */
  auction?: T | null;
}

/**
 * Whether a stored buyer's-premium config is structurally complete enough to
 * STOP the cascade — i.e. it carries a real, applicable premium.
 *
 * Private to this module by design: BL-983 AC 5 retires the exported
 * `isUsableBp`, because a predicate anyone can call is a predicate anyone can
 * build a fifth divergent chain out of. The semantics are unchanged from the
 * BUG-506/BUG-944 original:
 *
 * - Usable: a Flat schedule with a numeric `flatRate` — INCLUDING an explicit
 *   `0` (a deliberate operator waiver is honored, not overridden by a nonzero
 *   parent); a Tiered schedule with ≥1 tier, every tier's rate numeric.
 * - Not usable (falls through): `null`/`undefined`; `{}` with no `type`; a
 *   `{type:'Flat'}` with an absent or non-numeric rate (an absent rate carries
 *   no premium information and previously masked the tenant default at 0%);
 *   a Tiered schedule with no tiers or an empty array.
 *
 * Usability is about structural completeness, NOT about producing a non-zero
 * premium.
 */
function isUsableRung(bp: BuyersPremiumRungShape | PremiumSchedule | null | undefined): boolean {
  // BL-2482: a decoded PremiumSchedule is usable by construction (the wire codec
  // refuses a tier-less one, and every rate is a Rate) — an explicit 0% included.
  if (isPremiumSchedule(bp)) return true;
  // Delegates to the ONE structural predicate (`@auctogon/db/types/jsonb`) that the
  // calculator and the formatters also use, so the level the resolver picks and the
  // level those two agree to bill/render can never diverge. Kept as a named local
  // wrapper because BL-983 AC 5 deliberately does not export a *cascade* predicate —
  // what is shared is the structural test, not a resolution chain.
  return isUsableBuyersPremium(bp as BuyersPremiumLike);
}

/**
 * Resolve which level's buyer's premium applies, walking
 * **ring → auction** and returning the first structurally usable level along with
 * its identity.
 *
 * Every level is gated identically (BL-983, approved 2026-07-25). The retired
 * `resolveEffectiveBuyersPremium` deliberately passed its auction rung through
 * UNGATED to preserve pre-BUG-944 bidder output byte-for-byte; that per-level
 * exception is gone, because uniformity is the entire point of having one
 * resolver. The only observable delta is that a malformed level (e.g. a Flat
 * config with no rate) now falls through instead of being returned — it computed
 * to $0 either way, and BL-990's reset wiped those rows.
 *
 * Returns the caller's own object **by reference**, so the caller's concrete type
 * narrows through the generic (ADR 010 §2: generic-on-shape, so admin's
 * `BuyersPremiumSettings` and bidder's `PublicBuyersPremium` both pass unchanged).
 * There is nothing to normalize on the way out: BL-990 made every stored value
 * canonical on disk and deleted the read-seam bridge that used to repair it.
 *
 * @param rungs - The two cascade levels; omit or pass `null` for either that is
 *   unset. A soft-deleted ring MUST be passed as `null` (see
 *   {@link BuyersPremiumRungs.ring}).
 * @returns The winning config and its source, or `{bp: null, source: null}` when
 *   neither level carries a usable premium — which, for an auction, means it
 *   charges no buyer's premium (NOT "look upward"; BL-1382).
 * @sideEffect None — pure.
 */
export function resolveBuyersPremium<T extends BuyersPremiumRungShape | PremiumSchedule>(
  rungs: BuyersPremiumRungs<T>,
): ResolvedBuyersPremium<T> {
  // Ordered highest-precedence first; the first usable level wins.
  const cascade: ReadonlyArray<readonly [BuyersPremiumSource, T | null | undefined]> = [
    ['ring', rungs.ring],
    ['auction', rungs.auction],
  ];

  for (const [source, bp] of cascade) {
    if (isUsableRung(bp)) return { bp: bp as T, source };
  }

  return { bp: null, source: null };
}
